import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

interface CookieConsentContextType {
  hasConsent: boolean;
  consentType: 'accepted' | 'rejected' | 'custom' | null;
  preferences: CookiePreferences;
  showBanner: boolean;
  showSettingsModal: boolean;
  acceptAll: () => void;
  rejectAll: () => void;
  saveCustomPreferences: (prefs: CookiePreferences) => void;
  openSettingsModal: () => void;
  closeSettingsModal: () => void;
}

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

const STORAGE_KEYS = {
  CONSENT: 'cookie_consent',
  DATE: 'cookie_consent_date',
  PREFS: 'cookie_consent_prefs'
};

export function CookieConsentProvider({ children }: { children: ReactNode }) {
  const [hasConsent, setHasConsent] = useState(false);
  const [consentType, setConsentType] = useState<'accepted' | 'rejected' | 'custom' | null>(null);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false
  });
  const [showBanner, setShowBanner] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);

  useEffect(() => {
    const isAdminRoute = window.location.pathname.includes('/patronadmin');
    if (isAdminRoute) {
      setShowBanner(false);
      return;
    }

    const savedConsent = localStorage.getItem(STORAGE_KEYS.CONSENT);
    const savedPrefs = localStorage.getItem(STORAGE_KEYS.PREFS);

    if (savedConsent) {
      setHasConsent(true);
      setConsentType(savedConsent as 'accepted' | 'rejected' | 'custom');
      if (savedPrefs) {
        setPreferences(JSON.parse(savedPrefs));
      }
      setShowBanner(false);
    } else {
      setShowBanner(true);
    }
  }, []);

  const saveConsent = (type: 'accepted' | 'rejected' | 'custom', prefs: CookiePreferences) => {
    localStorage.setItem(STORAGE_KEYS.CONSENT, type);
    localStorage.setItem(STORAGE_KEYS.DATE, new Date().toISOString());
    localStorage.setItem(STORAGE_KEYS.PREFS, JSON.stringify(prefs));
    
    setHasConsent(true);
    setConsentType(type);
    setPreferences(prefs);
    setShowBanner(false);
    setShowSettingsModal(false);
  };

  const acceptAll = () => {
    saveConsent('accepted', {
      necessary: true,
      analytics: true,
      marketing: true
    });
  };

  const rejectAll = () => {
    saveConsent('rejected', {
      necessary: true,
      analytics: false,
      marketing: false
    });
  };

  const saveCustomPreferences = (prefs: CookiePreferences) => {
    saveConsent('custom', { ...prefs, necessary: true });
  };

  const openSettingsModal = () => {
    setShowSettingsModal(true);
  };

  const closeSettingsModal = () => {
    setShowSettingsModal(false);
  };

  return (
    <CookieConsentContext.Provider
      value={{
        hasConsent,
        consentType,
        preferences,
        showBanner,
        showSettingsModal,
        acceptAll,
        rejectAll,
        saveCustomPreferences,
        openSettingsModal,
        closeSettingsModal
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  const context = useContext(CookieConsentContext);
  if (context === undefined) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return context;
}